import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { IAnnualReportTemplateDTO } from '@modeso/aumico-lib-annualreport-fe';
import {
    AumicoCoreProvider,
    EMPTY_ARRAY_LENGTH,
    IAumMenuOptionsForRow,
    ITableOptions,
    NotificationType,
} from '@modeso/aumico-lib-core-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { IAccountingBasisDTO, ILegalFormDTO } from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import { BehaviorSubject } from 'rxjs';
import { annualReportTemplateActions } from '../../../actions/annual-report-templates.actions';
import { ArTemplatesListMenuActions } from '../../../enums/ar-template-list-menu-actions.enum';
import { AnnualReportTemplatesProvider } from '../../../provider/annual-report-templates.provider';
import { localizationKeys } from './../../../constants/localizationKeys';

const DELETE_SUCCESS_DESCRIPTION_LOCALIZATION_KEY =
    localizationKeys.aumico_admin_ar_template_list_delete_success_notification_description;
const DELETE_SUCCESS_TITLE_LOCALIZATION_KEY =
    localizationKeys.aumico_admin_ar_template_list_delete_success_notification_title;
const DELETE_ERROR_TITLE_LOCALIZATION_KEY =
    localizationKeys.aumico_admin_ar_template_list_delete_failed_notification_title;
const DELETE_ERROR_DESCRIPTION_LOCALIZATION_KEY =
    localizationKeys.aumico_admin_ar_template_list_delete_error_notification_description;
const DELETE_BUTTON_IN_MENU = localizationKeys.aumico_admin_ar_template_list_menu_item_delete;

@UntilDestroy()
@Component({
    selector: 'aumico-annualreport-ar-templates-list',
    templateUrl: './ar-templates-list.component.html',
    styleUrls: ['./ar-templates-list.component.scss'],
})
export class ArTemplatesListComponent implements OnInit, OnChanges {
    local = localizationKeys;
    private readonly localizationKeys = [
        this.local.aumico_admin_ar_template_general_information_accounting_bases,
        this.local.aumico_admin_ar_template_general_information_legal_forms,
        this.local.aumico_admin_ar_template_general_information_name,
        this.local.aumico_admin_ar_template_general_information_state,
        this.local.aumico_admin_ar_template_general_information_consistency,
        this.local.aumico_admin_ar_template_general_information_countries,
        this.local.aumico_admin_ar_template_table_actions,
    ];

    public options: ITableOptions | undefined;
    public tableData: IAnnualReportTemplateDTO[] = [];

    @Input() arTemplatesList: IAnnualReportTemplateDTO[] = [];

    @Input() accountingBases: IAccountingBasisDTO[] = [];
    @Input() legalForms: ILegalFormDTO[] = [];
    @Input() countries: INameId[] = [];

    @Input() currentLanguage = 'de-ch';
    @Input() filterSubject?: BehaviorSubject<string>;

    @Output() editArTemplateClicked: EventEmitter<string> = new EventEmitter();

    constructor(
        private readonly localizedTexts: I18NextService,
        private readonly annualReportTemplatesProvider: AnnualReportTemplatesProvider,
        private readonly actionsListener$: ActionsSubject,
        private readonly coreProvider: AumicoCoreProvider,
    ) {
        this.applyLocalizations();
    }

    ngOnInit(): void {
        this.actionsListener$
            .pipe(ofType(annualReportTemplateActions.deleteArTemplateFailed), untilDestroyed(this))
            .subscribe(() => {
                this.fireErrorNotification();
            });

        this.actionsListener$
            .pipe(ofType(annualReportTemplateActions.deleteArTemplateSuccess), untilDestroyed(this))
            .subscribe(() => {
                this.fireSuccessNotification();
                this.annualReportTemplatesProvider.dispatchArTemplatesList();
            });
    }

    ngOnChanges(): void {
        if (this.accountingBases.length > EMPTY_ARRAY_LENGTH && this.legalForms.length > EMPTY_ARRAY_LENGTH) {
            this.prepareTableData();
        }
    }

    private applyLocalizations(): void {
        this.prepareFields();
    }

    private prepareFields(): void {
        this.options = {
            columns: [
                {
                    name: this.localizedTexts.t(this.local.aumico_admin_ar_template_general_information_name) as string,
                    type: 'text',
                    property: 'name',
                    flex: 30,
                },
                {
                    name: this.localizedTexts.t(
                        this.local.aumico_admin_ar_template_general_information_legal_forms,
                    ) as string,
                    type: 'array',
                    property: 'legalFormsIds',
                    flex: 20,
                },
                {
                    name: this.localizedTexts.t(
                        this.local.aumico_admin_ar_template_general_information_accounting_bases,
                    ) as string,
                    type: 'array',
                    property: 'accountingBasesIds',
                    flex: 20,
                },
                {
                    name: this.localizedTexts.t(
                        this.local.aumico_admin_ar_template_general_information_countries,
                    ) as string,
                    type: 'array',
                    property: 'countryISO31661Alpha2Codes',
                    flex: 20,
                },
                {
                    name: this.localizedTexts.t(
                        this.local.aumico_admin_ar_template_general_information_state,
                    ) as string,
                    type: 'text',
                    property: 'state',
                    flex: 10,
                },
                {
                    name: this.localizedTexts.t(
                        this.local.aumico_admin_ar_template_general_information_consistency,
                    ) as string,
                    type: 'text',
                    property: 'consistency',
                    flex: 10,
                },
                {
                    name: this.localizedTexts.t(
                        this.local.aumico_admin_ar_template_list_reporting_template_name,
                    ) as string,
                    type: 'text',
                    property: 'reportingTemplateName',
                    flex: 10,
                },
                { name: 'actions' },
            ],
            menuPerRow: this.getMenuOptions(),
            rowClickAction: {
                property: 'id',
                action: 'edit',
            },
        };
    }

    private prepareTableData(): void {
        const data: IAnnualReportTemplateDTO[] = [];
        this.arTemplatesList.forEach((arTemplate) => {
            const legalForms = this.legalForms
                .filter((legalForm) => arTemplate.legalFormsIds.includes(legalForm._id))
                .map((legalForm) => legalForm.text[this.currentLanguage]);

            const accountingBases = this.accountingBases
                .filter((accountingBasis) => arTemplate.accountingBasesIds.includes(accountingBasis._id))
                .map((accountingBasis) => accountingBasis.text[this.currentLanguage]);

            const countries = this.countries
                .filter((country) => arTemplate.countryISO31661Alpha2Codes.includes(country.id))
                .map((country) => country.name);

            data.push({
                ...arTemplate,
                legalFormsIds: legalForms,
                accountingBasesIds: accountingBases,
                countryISO31661Alpha2Codes: countries,
            });
        });
        this.tableData = data;
    }

    private getMenuOptions(): IAumMenuOptionsForRow {
        return {
            items: [
                {
                    interchangeable: false,
                    caption: DELETE_BUTTON_IN_MENU,
                    action: ArTemplatesListMenuActions.DELETE,
                },
            ],
        };
    }

    private onMenuItemPerRowClick(action: string, arTemplateRow: IAnnualReportTemplateDTO): void {
        if (action === ArTemplatesListMenuActions.DELETE) {
            this.deleteArTemplate(arTemplateRow.id);
        }
    }

    private deleteArTemplate(id: string): void {
        this.annualReportTemplatesProvider.dispatchDeleteArTemplate(id);
    }

    onRowButtonClicked(id: string, action?: string): void {
        if (action === 'edit') {
            this.editArTemplateClicked.emit(id);
        }
    }

    private fireSuccessNotification(): void {
        this.coreProvider.dispatchAddNotification(
            DELETE_SUCCESS_TITLE_LOCALIZATION_KEY,
            DELETE_SUCCESS_DESCRIPTION_LOCALIZATION_KEY,
            NotificationType.SUCCESS,
        );
    }

    private fireErrorNotification(): void {
        this.coreProvider.dispatchAddNotification(
            DELETE_ERROR_TITLE_LOCALIZATION_KEY,
            DELETE_ERROR_DESCRIPTION_LOCALIZATION_KEY,
            NotificationType.ERROR,
        );
    }
}
